import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Chip,
} from '@mui/material'
import { apiClient } from '../services/api'

interface ApiTrafficItem {
    Id: string
    ApplicationId: string | null
    Direction: string
    BrokerName: string | null
    Endpoint: string
    Method: string
    StatusCode: number | null
    CreatedAt: string
}

const getStatusColor = (statusCode: number | null): 'default' | 'success' | 'error' | 'warning' => {
    if (!statusCode) return 'default'
    if (statusCode >= 200 && statusCode < 300) return 'success'
    if (statusCode >= 400 && statusCode < 500) return 'warning'
    if (statusCode >= 500) return 'error'
    return 'default'
}

const getDirectionColor = (direction: string): 'primary' | 'secondary' => {
    return direction === 'Received' ? 'primary' : 'secondary'
}

const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

export default function ApiTrafficPage() {
    const navigate = useNavigate()
    const [traffic, setTraffic] = useState<ApiTrafficItem[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetchTraffic = async () => {
            try {
                const data = await apiClient.getApiTraffic()
                setTraffic(data)
            } catch (err) {
                setError('Failed to load API traffic')
                console.error(err)
            } finally {
                setLoading(false)
            }
        }

        fetchTraffic()
    }, [])

    if (loading) {
        return (
            <Container maxWidth="lg">
                <CircularProgress />
            </Container>
        )
    }

    if (error) {
        return (
            <Container maxWidth="lg">
                <Alert severity="error">{error}</Alert>
            </Container>
        )
    }

    return (
        <Container maxWidth="xl">
            <Typography variant="h4" component="h1" gutterBottom>
                API Traffic
            </Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Date/Time</TableCell>
                            <TableCell>Direction</TableCell>
                            <TableCell>Broker</TableCell>
                            <TableCell>Endpoint</TableCell>
                            <TableCell>Method</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {traffic.map(item => (
                            <TableRow
                                key={item.Id}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/api-traffic/${item.Id}`)}
                            >
                                <TableCell>{formatDateTime(item.CreatedAt)}</TableCell>
                                <TableCell>
                                    <Chip label={item.Direction} color={getDirectionColor(item.Direction)} size="small" />
                                </TableCell>
                                <TableCell>{item.BrokerName || '-'}</TableCell>
                                <TableCell>{item.Endpoint}</TableCell>
                                <TableCell>
                                    <Chip label={item.Method} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>
                                    {item.StatusCode ? (
                                        <Chip label={item.StatusCode} color={getStatusColor(item.StatusCode)} size="small" />
                                    ) : (
                                        '-'
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Container>
    )
}
